import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import type { ItemOrcamento, DadosOrcamento, ConfigOrcamento } from '../../../types/orcamento';

interface PlanilhaOrcamentariaProps {
  value: DadosOrcamento;
  onChange: (data: DadosOrcamento) => void;
  config?: ConfigOrcamento;
  readonly?: boolean;
}

export default function PlanilhaOrcamentaria({
  value = { itens: [], resumo: { total_geral: 0, totais_por_categoria: {} as any } },
  onChange,
  config,
  readonly = false
}: PlanilhaOrcamentariaProps) {
  const [itens, setItens] = useState<ItemOrcamento[]>(value?.itens || []);
  const [observacoes, setObservacoes] = useState(value?.observacoes || '');
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);

  // Calcular totais automaticamente
  useEffect(() => {
    const total_geral = itens.reduce((sum, item) => sum + item.valor_total, 0);
    const totais_por_categoria: Record<string, number> = {};

    itens.forEach(item => {
      if (!totais_por_categoria[item.categoria]) {
        totais_por_categoria[item.categoria] = 0;
      }
      totais_por_categoria[item.categoria] += item.valor_total;
    });

    onChange({
      itens,
      resumo: {
        total_geral: parseFloat(total_geral.toFixed(2)),
        totais_por_categoria
      },
      anexos: value.anexos || [],
      observacoes
    });
  }, [itens, observacoes]);

  const adicionarItem = () => {
    const novoItem: ItemOrcamento = {
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      categoria: 'pessoal',
      descricao: '',
      especificacao: '',
      unidade: 'un',
      quantidade: 1,
      valor_unitario: 0,
      valor_total: 0,
      justificativa: ''
    };
    setItens([...itens, novoItem]);
    setItemExpandido(novoItem.id);
  };

  const removerItem = (id: string) => {
    if (window.confirm('Deseja remover este item?')) {
      setItens(itens.filter(item => item.id !== id));
      if (itemExpandido === id) {
        setItemExpandido(null);
      }
    }
  };

  const atualizarItem = (id: string, campo: string, valor: any) => {
    setItens(itens.map(item => {
      if (item.id === id) {
        const itemAtualizado = { ...item, [campo]: valor };
        
        // Recalcular valor total
        if (campo === 'quantidade' || campo === 'valor_unitario') {
          itemAtualizado.valor_total = parseFloat(
            (itemAtualizado.quantidade * itemAtualizado.valor_unitario).toFixed(2)
          );
        }
        
        return itemAtualizado;
      }
      return item;
    }));
  };

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const totalGeral = itens.reduce((sum, item) => sum + item.valor_total, 0);
  const podeAdicionar = !readonly && (!config?.validacoes?.max_itens || itens.length < (config?.validacoes?.max_itens || 0));

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Planilha Orçamentária
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {itens.length} {itens.length === 1 ? 'item' : 'itens'} • Total: {formatarMoeda(totalGeral)}
          </p>
        </div>
        
        {podeAdicionar && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              adicionarItem();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            Adicionar Item
          </button>
        )}
      </div>

      {/* Validações */}
      {config?.validacoes?.valor_minimo_total && totalGeral < config.validacoes.valor_minimo_total && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            ⚠️ O valor total deve ser no mínimo {formatarMoeda(config.validacoes.valor_minimo_total)}
          </p>
        </div>
      )}

      {config?.validacoes?.valor_maximo_total && totalGeral > config.validacoes.valor_maximo_total && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            ❌ O valor total não pode exceder {formatarMoeda(config.validacoes.valor_maximo_total)}
          </p>
        </div>
      )}

      {/* Tabela de Itens */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidade</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qtd</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Unit.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Total</th>
              {!readonly && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {itens.map((item) => (
              <React.Fragment key={item.id}>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {readonly ? (
                      <span className="text-sm">{config?.categorias?.find(c => c.value === item.categoria)?.label || item.categoria}</span>
                    ) : (
                      <select
                        value={item.categoria}
                        onChange={(e) => atualizarItem(item.id, 'categoria', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Selecione...</option>
                        {config?.categorias?.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        )) || []}
                      </select>
                    )}
                  </td>
                  
                  <td className="px-4 py-3">
                    {readonly ? (
                      <span className="text-sm">{item.descricao}</span>
                    ) : (
                      <input
                        type="text"
                        value={item.descricao}
                        onChange={(e) => atualizarItem(item.id, 'descricao', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                        placeholder="Descrição do item"
                      />
                    )}
                  </td>
                  
                  <td className="px-4 py-3">
                    {readonly ? (
                      <span className="text-sm">{item.unidade}</span>
                    ) : (
                      <select
                        value={item.unidade}
                        onChange={(e) => atualizarItem(item.id, 'unidade', e.target.value)}
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="un">un</option>
                        <option value="cx">cx</option>
                        <option value="kg">kg</option>
                        <option value="m">m</option>
                        <option value="m²">m²</option>
                        <option value="hora">hora</option>
                        <option value="mês">mês</option>
                        <option value="ano">ano</option>
                      </select>
                    )}
                  </td>
                  
                  <td className="px-4 py-3">
                    {readonly ? (
                      <span className="text-sm">{item.quantidade}</span>
                    ) : (
                      <input
                        type="number"
                        value={item.quantidade}
                        onChange={(e) => atualizarItem(item.id, 'quantidade', parseInt(e.target.value) || 0)}
                        className="w-20 px-2 py-1 border rounded text-sm"
                        min="0"
                        step="1"
                      />
                    )}
                  </td>
                  
                  <td className="px-4 py-3">
                    {readonly ? (
                      <span className="text-sm">{formatarMoeda(item.valor_unitario)}</span>
                    ) : (
                      <input
                        type="number"
                        value={item.valor_unitario}
                        onChange={(e) => atualizarItem(item.id, 'valor_unitario', parseFloat(e.target.value) || 0)}
                        className="w-28 px-2 py-1 border rounded text-sm"
                        min="0"
                        step="0.01"
                      />
                    )}
                  </td>
                  
                  <td className="px-4 py-3">
                    <span className="font-semibold text-sm">{formatarMoeda(item.valor_total)}</span>
                  </td>
                  
                  {!readonly && (
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setItemExpandido(itemExpandido === item.id ? null : item.id);
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title="Ver detalhes"
                        >
                          {itemExpandido === item.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            removerItem(item.id);
                          }}
                          className="text-red-600 hover:text-red-800"
                          title="Remover"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                
                {/* Linha expandida com detalhes */}
                {(itemExpandido === item.id || (readonly && (item.especificacao || item.justificativa))) && (
                  <tr>
                    <td colSpan={readonly ? 7 : 8} className="px-4 py-4 bg-gray-50">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Especificação
                          </label>
                          {readonly ? (
                            <p className="text-sm text-gray-900">{item.especificacao || 'Não informada'}</p>
                          ) : (
                            <textarea
                              value={item.especificacao || ''}
                              onChange={(e) => atualizarItem(item.id, 'especificacao', e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                              rows={2}
                              placeholder="Detalhes técnicos, marca, modelo..."
                            />
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Justificativa *
                          </label>
                          {readonly ? (
                            <p className="text-sm text-gray-900">{item.justificativa || 'Não informada'}</p>
                          ) : (
                            <textarea
                              value={item.justificativa}
                              onChange={(e) => atualizarItem(item.id, 'justificativa', e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                              rows={3}
                              placeholder="Explique por que este item é necessário para o projeto..."
                              required
                            />
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          
          {/* Footer com totais */}
          {itens.length > 0 && (
            <tfoot className="bg-gray-100">
              <tr>
                <td colSpan={5} className="px-4 py-3 text-right font-semibold">TOTAL GERAL:</td>
                <td className="px-4 py-3 font-bold text-lg">{formatarMoeda(totalGeral)}</td>
                {!readonly && <td></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Totais por Categoria */}
      {itens.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Resumo por Categoria</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {config?.categorias?.map(cat => {
              const total = itens
                .filter(item => item.categoria === cat.value)
                .reduce((sum, item) => sum + item.valor_total, 0);
              
              if (total === 0) return null;
              
              return (
                <div key={cat.value} className="bg-white rounded p-3">
                  <p className="text-xs text-gray-600">{cat.label}</p>
                  <p className="text-lg font-semibold text-blue-600">{formatarMoeda(total)}</p>
                </div>
              );
            }) || []}
          </div>
        </div>
      )}

      {/* Observações */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Observações Gerais
        </label>
        {readonly ? (
          <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">
            {observacoes || 'Nenhuma observação informada'}
          </p>
        ) : (
          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            rows={3}
            placeholder="Informações adicionais sobre o orçamento..."
          />
        )}
      </div>

      {/* Mensagem se vazio */}
      {itens.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Nenhum item adicionado ainda.</p>
          {!readonly && (
            <p className="text-sm text-gray-400 mt-2">Clique em "Adicionar Item" para começar.</p>
          )}
        </div>
      )}
    </div>
  );
}
